import { Injectable } from 'framework/di';
import { pipe } from 'framework/functional';

import { BoostAPI } from '../app/app.api';
import { AppService } from '../app/app.service';
import { B2BService } from '../b2b/b2b.service';
import { PlatformLoader } from '../platform/platform.loader';

/**
 * API service for fetching product details used in Quick View modal.
 *
 * Returns pre-rendered HTML from server including product images, variant swatches,
 * price display, and add to cart functionality.
 *
 * @example
 * Extend to add custom query parameters:
 * ```javascript
 * window.boostWidgetIntegration.extend('ProductAPI', (ProductAPI) => {
 *   return class CustomProductAPI extends ProductAPI {
 *     protected buildQuickViewQueryParams(params) {
 *       const enhanced = super.buildQuickViewQueryParams(params);
 *       const customer = this.platformLoader.platform.customer;
 *       if (customer?.tags?.includes('wholesale')) {
 *         enhanced.pricing_tier = 'wholesale';
 *       }
 *       return enhanced;
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class ProductAPI extends BoostAPI {
  constructor(
    protected platformLoader: PlatformLoader,
    protected appService: AppService,
    protected b2bService: B2BService
  ) {
    super(platformLoader);
    this.config.baseURL = this.appService.TAEAppConfig.productUrl;
  }

  protected buildQuickViewQueryParams(params: ProductQueryParams): ProductQueryParams {
    return pipe(
      { ...params },
      (p) => this.addTenantParams(p),
      (p) => this.addLocalizationParams(p),
      (p) => this.addCurrencyParams(p),
      (p) => this.addCustomerIdParam(p),
      (p) => this.b2bService.addB2BRequestParams(p)
    );
  }

  async getProductDetail(params: ProductQueryParams): Promise<string> {
    const enhancedParams = this.buildQuickViewQueryParams(params);
    const queryParams = this.buildDefaultQueryParams(enhancedParams, { includeSearchQuery: false });

    const response = await this.get<string>(`/?${queryParams}`);
    return response.data;
  }
}

export type ProductQueryParams = {
  product_id: string | number;
  quickview_widget_id: string;
};
